import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/UnitList.css";

const RoomBookingList = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "";
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  const fetchBookings = async (pageToFetch = page) => {
    try {
      const response = await fetch(
        `${API_URL}list-bed-bookings.php?page=${pageToFetch}&limit=10`
      );
      const data = await response.json();
      if (Array.isArray(data.data)) {
        setBookings(data.data);
        setTotalPages(Math.ceil(data.total_rows / data.limit));
      } else {
        showToast("Failed to load bookings", "error");
      }
    } catch (error) {
      showToast("Network error fetching bookings", "error");
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line
  }, [page, API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const filteredBookings = bookings.filter(
    (booking) =>
      booking.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.room_no &&
        booking.room_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (booking.bed_no &&
        booking.bed_no.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddBooking = () => {
    navigate("/add-room-booking");
  };

  const handleDelete = (bookingId) => {
    setBookingToDelete(bookingId);
    setDeleteModalOpen(true);
  };

  const handleView = (bookingId) => {
    const found = bookings.find(b => b.id === bookingId);
    setBookingDetails(found || null);
    setDetailsModalOpen(true);
  };

  const openModal = (imageName) => {
    setModalImage(`${API_URL}uploads/${imageName}`);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage("");
  };

  const formatDateTime12Hour = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr.replace(/-/g, '/'));
    if (isNaN(date.getTime())) return dateTimeStr;
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString(undefined, options);
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Room Bookings</h1>
          <p>View and manage all customer bookings</p>
        </div>
        <button className="add-unit-button" onClick={handleAddBooking}>
          + Add New Booking
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by customer, room, or bed..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Customer Details</th>
              <th>Room / Bed</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Total Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map((booking) => (
              <tr key={booking.id}>
                <td>
                  <div className="unit-info">
                    <div
                      className="unit-icon"
                      style={{
                        backgroundImage: `url(${API_URL}uploads/${booking.id_proof_image})`,
                        backgroundSize: "cover",
                        cursor: "pointer",
                      }}
                      onClick={() => openModal(booking.id_proof_image)}
                    ></div>
                    <div>
                      <strong>{booking.customer_name}</strong>
                      <p>{booking.address}</p>
                    </div>
                  </div>
                </td>
                <td>
                  {booking.room_no || "N/A"} / {booking.bed_no || "N/A"}
                </td>
                <td>{new Date(booking.checking_date).toLocaleDateString()}</td>
                <td>{new Date(booking.checkout_date).toLocaleDateString()}</td>
                <td>₹{booking.total_amount}</td>
                <td>
                  <div className="action-buttons">
                  <button
                      className="action-btn view"
                      onClick={() => handleView(booking.id)}
                    >
                      View
                    </button>
                    <button
                      className="action-btn edit"
                      onClick={() => navigate(`/edit-booking/${booking.id}`)}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(booking.id)}
                    >
                      Delete
                    </button>
                    
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Page {page} of {totalPages}
        </div>
        <div className="pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              {" "}
              ×{" "}
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "none",
              color: "white",
              border: "none",
              fontSize: "3em",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
          <img
            src={modalImage}
            alt="ID Proof"
            style={{
              maxWidth: "85%",
              maxHeight: "85%",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {detailsModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1001,
          }}
          onClick={() => setDetailsModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "2em",
              borderRadius: "12px",
              minWidth: "650px",
              maxWidth: "95vw",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              fontFamily: "Segoe UI, Arial, sans-serif"
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setDetailsModalOpen(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "20px",
                background: "none",
                color: "#333",
                border: "none",
                fontSize: "2em",
                cursor: "pointer",
              }}
            >
              &times;
            </button>
            {bookingDetails ? (
              <div>
                <h2 style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  marginBottom: "1.5rem",
                  letterSpacing: "0.5px",
                  color: "#222"
                }}>Booking Details</h2>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}>
                  <tbody>
                    {[
                      ["Customer Name", bookingDetails.customer_name],
                      ["Address", bookingDetails.address],
                      ["ID Proof Type", bookingDetails.id_proof_type],
                      ["ID Proof Image", (
                        <img
                          src={`${API_URL}uploads/${bookingDetails.id_proof_image}`}
                          alt="ID Proof"
                          style={{
                            maxWidth: "120px",
                            maxHeight: "120px",
                            borderRadius: '6px',
                            border: '1px solid #eee',
                            display: "block",
                            margin: "0.5em auto"
                          }}
                        />
                      )],
                      ["Room No", bookingDetails.room_no],
                      ["Bed No", bookingDetails.bed_no],
                      ["Check-in", bookingDetails.checking_date],
                      ["Check-out", bookingDetails.checkout_date],
                      ["No. of Days", bookingDetails.nodays],
                      ["Per Day Price", `₹${bookingDetails.per_day_price}`],
                      ["Total Amount", `₹${bookingDetails.total_amount}`],
                      ["Customer Details", bookingDetails.customer_details],
                      ["Customer E-Pass", (
                        <img
                          src={`${API_URL}uploads/${bookingDetails.customer_getepass}`}
                          alt="E-Pass"
                          style={{
                            maxWidth: "120px",
                            maxHeight: "120px",
                            borderRadius: '6px',
                            border: '1px solid #eee',
                            display: "block",
                            margin: "0.5em auto"
                          }}
                        />
                      )],
                      ["Created At", formatDateTime12Hour(bookingDetails.created_at)]
                    ].map(([label, value], idx) => (
                      <tr key={label} style={{
                        background: idx % 2 === 0 ? "#fafbfc" : "#fff"
                      }}>
                        <td style={{
                          fontWeight: 600,
                          padding: '12px 16px',
                          border: 'none',
                          color: "#333",
                          width: "200px",
                          background: "#f4f6f8"
                        }}>{label}</td>
                        <td style={{
                          padding: '12px 16px',
                          border: 'none',
                          color: "#222"
                        }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>No details found.</div>
            )}
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
          onClick={() => setDeleteModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "2em",
              borderRadius: "8px",
              minWidth: "320px",
              position: "relative",
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this booking?</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1em", marginTop: "1.5em" }}>
              <button
                style={{ background: "#ccc", border: "none", padding: "0.5em 1.2em", borderRadius: "4px", cursor: "pointer" }}
                onClick={() => setDeleteModalOpen(false)}
              >
                No
              </button>
              <button
                style={{ background: "#e74c3c", color: "#fff", border: "none", padding: "0.5em 1.2em", borderRadius: "4px", cursor: "pointer" }}
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_URL}bed-booking-delete.php`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ booking_id: bookingToDelete }),
                    });
                    const data = await response.json();
                    if (data.success) {
                      showToast("Booking deleted successfully!", "success");
                      fetchBookings(1); // Refresh the list, optionally reset to first page
                      setPage(1);
                    } else {
                      showToast(data.message || "Delete failed.", "error");
                    }
                  } catch (error) {
                    showToast("Network error. Could not delete booking.", "error");
                  }
                  setDeleteModalOpen(false);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomBookingList;
